import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Typography } from '@mui/material';

import { sleep } from '@altirllc/shared/utils/functionalUtils';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';

import { toFullName } from '@/utils/stringUtils';
import { CircularProgress } from '@altirllc/shared/storybook/stories/CircularProgress.stories';

import { Cancel } from '@/components/icons/Cancel';
import { GenericModal } from '@/components/modal/GenericModal';
import { GenericModalClose } from '@/components/modal/GenericModalClose';
import { GenericModalFooter } from '@/components/modal/GenericModalFooter';

import type { TMember } from '@/types/member';

import { styles } from './MemberModal.styles';

type TSuspendMemberProps = TModalsContext & {
  member: TMember;
  onSuccess: (member: TMember) => void;
};

export const SuspendMember = ({ close, member, onSuccess }: TSuspendMemberProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const name = toFullName(member);

  const closeSelf = () => close('members.suspend');

  const onSubmit = async () => {
    setLoading(true);
    await sleep(500);

    setLoading(false);
    onSuccess(member);
    closeSelf();
  };

  return (
    <GenericModal sx={[styles.modal, styles.body, { minHeight: 405 }]}>
      <GenericModalClose onClick={closeSelf} />

      <Box sx={styles.fields}>
        <Cancel sx={styles.actionIcon} />

        <Typography variant="h5" marginBottom={1.5}>
          {t('members.suspend.title')}
        </Typography>

        <Typography color="text.secondary">{t('members.suspend.details', { name })}</Typography>
      </Box>

      <GenericModalFooter placeContent="end">
        <Button variant="outlined" onClick={closeSelf}>
          {t('common.cancel')}
        </Button>

        <Button variant="outlined" onClick={onSubmit} color="error" disabled={loading}>
          {t('members.suspend.submit')}
          {loading && <CircularProgress />}
        </Button>
      </GenericModalFooter>
    </GenericModal>
  );
};
